import React, { useState, useMemo } from "react";
import {
  Box,
  Typography,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Switch,
  FormControlLabel,
  Tooltip,
} from "@mui/material";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import { useTheme } from "@mui/material/styles";
import { DatePivotParams } from "../../entities/date_pivot_params";
import CompactTableTextField from "../../components/transformed_custom_view/viewer/CompactTableTextField";
import { LoadingState } from "../../components/transformed_custom_view/viewer/LoadingState";
import dayjs from "dayjs";
import { shortFormatDateString } from "../../utils/date_format";
import { getRowFromArrowTable } from "../../utils/mui_table_utils";
import * as arrow from "apache-arrow";
import {
  getSupplyCellBackgroundColor,
  getWOSMeasureKey,
} from "../../utils/supply_color_coding";

interface SupplyWalkSingleViewerComponentProps {
  parsedData: {
    arrowTable: arrow.Table | null;
    columns: arrow.Field<any>[] | null;
  };
  loading: boolean;
  error: string | null;
  viewParams: DatePivotParams | null;
}

interface SegmentationCombination {
  segmentValues: Record<string, string>;
  segmentationRank: number;
  displayKey: string;
}

// Helper to cascade recalculation after an edit
const cascadeRecalculate = (
  measure: string,
  date: string,
  newValue: string,
  editedValues: Record<string, Record<string, string>>,
  rawData: any,
  allDates: string[],
  viewParamsArg: DatePivotParams
) => {
  const startIdx = allDates.indexOf(date);
  if (startIdx === -1) return editedValues;

  // (Optional) Debug trace
  traceCascadeOrder(
    measure,
    date,
    newValue,
    editedValues,
    rawData,
    allDates,
    viewParamsArg
  );

  // Start with a copy of the edited values, and apply the new edit
  let newEdited = { ...editedValues };
  newEdited = {
    ...newEdited,
    [measure]: { ...(newEdited[measure] || {}), [date]: newValue },
  };

  // Helper to get the most up-to-date value for a measure/date
  const getInt = (m: string, dt: string) => {
    const v = newEdited[m]?.[dt] ?? rawData.data[m][dt] ?? "";
    const s = typeof v === "string" ? v.replace(/,/g, "") : v;
    return s !== "" && !isNaN(Number(s)) ? parseInt(s, 10) : 0;
  };

  // Spoke demand-out is just direct sales.
  const demandOut = (dt: string) => Math.abs(getInt("forecasted sales", dt));

  // --- STEP 1: Update the edited week ---
  const currentStartingInv = getInt("starting inventory on hand", date);
  const currentDemand = demandOut(date);
  const currentPOArrivals = getInt("purchase order arrivals", date);

  const calcEndingInv = Math.max(
    0,
    currentStartingInv - currentDemand + currentPOArrivals
  );
  newEdited = {
    ...newEdited,
    "ending inventory on hand": {
      ...(newEdited["ending inventory on hand"] || {}),
      [date]: String(calcEndingInv),
    },
  };

  const calcUnfulfilled = Math.max(
    0,
    currentDemand - currentStartingInv - currentPOArrivals
  );
  newEdited = {
    ...newEdited,
    "unfulfilled demand": {
      ...(newEdited["unfulfilled demand"] || {}),
      [date]: String(calcUnfulfilled),
    },
  };

  const absFS = currentDemand;
  const percent =
    absFS === 0 ? 100 : Math.round(((absFS - calcUnfulfilled) / absFS) * 100);
  newEdited = {
    ...newEdited,
    "in stock rate": {
      ...(newEdited["in stock rate"] || {}),
      [date]: percent + "%",
    },
  };

  // --- STEP 2: Cascade forward ---
  // For each future week, set starting inventory to the just-calculated ending inventory from the previous week
  let prevEndingInv = calcEndingInv;
  for (let i = startIdx + 1; i < allDates.length; i++) {
    const nextDate = allDates[i];

    // Assign prev ending inventory directly (guarantees strict forward dependency)
    newEdited = {
      ...newEdited,
      "starting inventory on hand": {
        ...(newEdited["starting inventory on hand"] || {}),
        [nextDate]: String(prevEndingInv),
      },
    };

    // Use the just-assigned starting inventory for all calculations
    const nextStartingInv = prevEndingInv;
    const nextDemand = demandOut(nextDate);
    const nextPOArrivals = getInt("purchase order arrivals", nextDate);

    // ending inventory = starting inventory - demand_out + purchase order arrivals
    // (demand_out = sales, plus transfer departures at a hub)
    const nextEndingInv = Math.max(
      0,
      nextStartingInv - nextDemand + nextPOArrivals
    );

    const nextUnfulfilled = Math.max(
      0,
      nextDemand - nextStartingInv - nextPOArrivals
    );

    const nextAbsFS = nextDemand;
    const nextPercent =
      nextAbsFS === 0
        ? 100
        : Math.round(((nextAbsFS - nextUnfulfilled) / nextAbsFS) * 100);

    newEdited = {
      ...newEdited,
      "ending inventory on hand": {
        ...(newEdited["ending inventory on hand"] || {}),
        [nextDate]: String(nextEndingInv),
      },
      "unfulfilled demand": {
        ...(newEdited["unfulfilled demand"] || {}),
        [nextDate]: String(nextUnfulfilled),
      },
      "in stock rate": {
        ...(newEdited["in stock rate"] || {}),
        [nextDate]: nextPercent + "%",
      },
    };

    prevEndingInv = nextEndingInv;
  }

  // --- STEP 3: Recalculate weeks of supply ---
  // Ensure WOS uses the freshest values from newEdited after all other calcs
  const getFreshInt = (m: string, dt: string) => {
    const v = newEdited[m]?.[dt] ?? rawData.data[m][dt] ?? "";
    const s = typeof v === "string" ? v.replace(/,/g, "") : v;
    return s !== "" && !isNaN(Number(s)) ? parseInt(s, 10) : 0;
  };

  const absForecastedSales = allDates.map((date) =>
    Math.abs(getFreshInt("forecasted sales", date))
  );

  // Fractional weeks-of-supply: count the whole periods the inventory fully
  // covers, then add the partial fraction of the period where it runs out
  // (remaining / that period's demand). Matches the engine's fractional WOS;
  // counting only whole periods rounded e.g. 1.4 down to 1.
  const computeWOS = (inventory: number, fromIdx: number) => {
    let remaining = inventory;
    let weeks = 0;
    for (let bIdx = fromIdx; bIdx < allDates.length; bIdx++) {
      const periodDemand = absForecastedSales[bIdx];
      if (periodDemand <= 0) {
        weeks += 1;
        continue;
      }
      if (remaining >= periodDemand) {
        weeks += 1;
        remaining -= periodDemand;
      } else {
        weeks += remaining / periodDemand;
        break;
      }
    }
    return weeks;
  };

  // Starting WOS counts from the current period; ending WOS from the next.
  const startingWOSArray = allDates.map((date, aIdx) =>
    computeWOS(getFreshInt("starting inventory on hand", date), aIdx)
  );
  const endingWOSArray = allDates.map((date, aIdx) =>
    computeWOS(getFreshInt("ending inventory on hand", date), aIdx + 1)
  );

  for (let i = 0; i < allDates.length; i++) {
    newEdited = {
      ...newEdited,
      [getWOSMeasureKey("starting", viewParamsArg.dateHierarchy)]: {
        ...(newEdited[
          getWOSMeasureKey("starting", viewParamsArg.dateHierarchy)
        ] || {}),
        [allDates[i]]: startingWOSArray[i].toFixed(1),
      },
      [getWOSMeasureKey("ending", viewParamsArg.dateHierarchy)]: {
        ...(newEdited[
          getWOSMeasureKey("ending", viewParamsArg.dateHierarchy)
        ] || {}),
        [allDates[i]]: endingWOSArray[i].toFixed(1),
      },
    };
  }

  return newEdited;
};

// Debug function to trace cascade order of operations
const traceCascadeOrder = (
  measure: string,
  date: string,
  newValue: string,
  editedValues: Record<string, Record<string, string>>,
  rawData: any,
  allDates: string[],
  viewParamsArg: DatePivotParams
) => {
  const startIdx = allDates.indexOf(date);
  if (startIdx === -1) return;

  let newEdited = { ...editedValues };
  newEdited = {
    ...newEdited,
    [measure]: { ...(newEdited[measure] || {}), [date]: newValue },
  };

  const getInt = (m: string, dt: string) => {
    const v = newEdited[m]?.[dt] ?? rawData.data[m][dt] ?? "";
    const s = typeof v === "string" ? v.replace(/,/g, "") : v;
    return s !== "" && !isNaN(Number(s)) ? parseInt(s, 10) : 0;
  };

  // STEP 1: Current period

  // STEP 2: Cascade forward

  for (let i = startIdx + 1; i < allDates.length; i++) {
    const nextDate = allDates[i];
    const prevDate = allDates[i - 1];

    // Step 2a: Set starting inventory
    const prevEndingInv = getInt("ending inventory on hand", prevDate);

    newEdited = {
      ...newEdited,
      "starting inventory on hand": {
        [nextDate]: String(prevEndingInv),
      },
    };

    // Step 2b: Calculate ending inventory
    const nextStartingInv = getInt("starting inventory on hand", nextDate);
    const nextForecastedSales = getInt("forecasted sales", nextDate);
    const nextPOArrivals = getInt("purchase order arrivals", nextDate);
    const nextEndingInv = Math.max(
      0,
      nextStartingInv - Math.abs(nextForecastedSales) + nextPOArrivals
    );

    newEdited = {
      ...newEdited,
      "ending inventory on hand": {
        ...(newEdited["ending inventory on hand"] || {}),
        [nextDate]: String(nextEndingInv),
      },
    };

    // Step 2c: Calculate unfulfilled demand
    const nextUnfulfilled = Math.max(
      0,
      Math.abs(nextForecastedSales) - nextStartingInv - nextPOArrivals
    );

    newEdited = {
      ...newEdited,
      "unfulfilled demand": {
        ...(newEdited["unfulfilled demand"] || {}),
        [nextDate]: String(nextUnfulfilled),
      },
    };
  }
};

// Helper to find the arrival week given a placement date and lead time in days
const findArrivalWeek = (
  placementDateEpoch: string,
  leadTimeDays: number,
  allDates: string[]
) => {
  const placementDate = new Date(Number(placementDateEpoch));
  const arrivalDate = new Date(placementDate);
  arrivalDate.setDate(arrivalDate.getDate() + leadTimeDays);
  // Land the PO in the period bucket that CONTAINS the arrival date: the last
  // column whose start is <= arrivalDate. Columns are bucket-start dates, so an
  // arrival mid-bucket belongs to that bucket — picking the first column >=
  // arrival pushed the arrival one bucket late.
  const arrivalEpoch = arrivalDate.getTime();
  let bestDate = allDates[0];
  for (const date of allDates) {
    if (Number(date) <= arrivalEpoch) {
      bestDate = date;
    } else {
      break;
    }
  }
  return bestDate;
};

// Update getPeriodLabel to require a DatePivotParams argument
const getPeriodLabel = (viewParamsArg: DatePivotParams) => {
  if (!viewParamsArg?.dateHierarchy) return "weeks";
  if (viewParamsArg.dateHierarchy.includes("day")) return "days";
  if (viewParamsArg.dateHierarchy.includes("week")) return "weeks";
  if (viewParamsArg.dateHierarchy.includes("month")) return "months";
  return "periods";
};

// Update getDynamicMeasureLabel to require viewParams and call getPeriodLabel(viewParams)
const getDynamicMeasureLabel = (
  measure: string,
  viewParamsArg: DatePivotParams
) => {
  const period = getPeriodLabel(viewParamsArg);
  if (measure.toLowerCase().includes("target weeks of supply"))
    return `target ${period} of supply`;
  if (measure.toLowerCase().includes("starting weeks of supply"))
    return `starting ${period} of supply`;
  if (measure.toLowerCase().includes("ending weeks of supply"))
    return `ending ${period} of supply`;
  return measure;
};

// Helper to get the current Days Lead Time value for a segmentation
const getLeadTimeValue = (
  editedValues: any,
  rawData: any,
  displayKey: string,
  date: string
): string => {
  return (
    editedValues?.[displayKey]?.["lead time"]?.[date] ??
    rawData?.data?.["lead time"]?.[date] ??
    "90"
  );
};

// Helper to format numbers as comma-separated integers
const formatInt = (val: string | number): string => {
  if (val === null || val === undefined || val === "") return "";
  const num = Number(String(val).replace(/,/g, ""));
  if (isNaN(num)) return String(val);
  return num.toLocaleString("en-US", { maximumFractionDigits: 0 });
};

export const SupplyWalkSingleViewerComponent = ({
  parsedData,
  loading,
  error,
  viewParams,
}: SupplyWalkSingleViewerComponentProps) => {
  const arrowTable = parsedData?.arrowTable;
  const columns = parsedData?.columns;

  const theme = useTheme();
  const [currentIndex, setCurrentIndex] = useState(0);
  // Track edited values per segmentation combination: segmentationKey -> measure -> date -> value
  const [editedValues, setEditedValues] = useState<
    Record<string, Record<string, Record<string, string>>>
  >({});
  // Edit mode toggle
  const [editMode, setEditMode] = useState(false);
  // Local state for input values to prevent focus loss
  const [localInputValues, setLocalInputValues] = useState<
    Record<string, Record<string, Record<string, string>>>
  >({});

  // Helper to get background color for a cell based on measure/value/highlight, using only theme.palette.atomic colors
  const getCellBackgroundColor = (options: {
    measure: string;
    value: number;
    targetWOS?: number | null;
    highlight?: boolean;
  }) => {
    const { measure, value, targetWOS, highlight } = options;
    const m = measure.toLowerCase();

    // WOS color logic using new utility function
    if (
      m.includes("weeks of supply") ||
      m.includes("days of supply") ||
      m.includes("months of supply")
    ) {
      if (targetWOS !== undefined && targetWOS !== null && !isNaN(targetWOS)) {
        return getSupplyCellBackgroundColor({
          value,
          targetValue: targetWOS,
          theme,
        });
      }
    }

    // PO placed/arrivals highlight
    if (highlight) return theme.palette.atomic.orange1;
    // Default: neutral
    return undefined;
  };

  const normalizeSegmentValue = (v: unknown): string =>
    v == null || v === "" ? "" : String(v).trim();

  // Extract unique segmentation combinations and sort by segmentation rank
  const segmentationCombinations = useMemo(() => {
    if (
      !arrowTable ||
      !columns ||
      !viewParams?.segments ||
      viewParams.segments.length === 0
    ) {
      console.warn(
        "[SupplyWalkSingleViewerComponent] No arrowTable/columns/segments"
      );
      return [];
    }

    const colNames = new Set(columns.map((c) => c.name));
    const hasSegmentationCol = colNames.has("segmentation");
    const segmentColsMissing = viewParams.segments!.some(
      (s) => !colNames.has(s)
    );

    const combinations = new Map<string, SegmentationCombination>();

    const numRows = arrowTable.numRows;
    for (let i = 0; i < numRows; i++) {
      const row = getRowFromArrowTable(arrowTable, columns, i);
      const segmentValues: Record<string, string> = {};
      let displayKey = "";

      if (hasSegmentationCol && segmentColsMissing) {
        // Parquet has a combined "segmentation" column instead of individual
        // dimension columns. Parse it back into per-segment values.
        const segStr = normalizeSegmentValue(row.segmentation);
        const parts = segStr.split(" | ");
        viewParams.segments!.forEach((segment, index) => {
          const value = parts[index] ?? "";
          segmentValues[segment] = value;
          displayKey += (index > 0 ? " | " : "") + value;
        });
      } else {
        viewParams.segments!.forEach((segment, index) => {
          const value = normalizeSegmentValue(row[segment]);
          segmentValues[segment] = value;
          displayKey += (index > 0 ? " | " : "") + value;
        });
      }

      const segmentationRank =
        row.segmentation_rank || row.segmentationRank || 0;
      const key = displayKey;

      if (!combinations.has(key)) {
        combinations.set(key, {
          segmentValues,
          segmentationRank,
          displayKey,
        });
      }
    }

    const result = Array.from(combinations.values()).sort(
      (a, b) => a.segmentationRank - b.segmentationRank
    );

    return result;
  }, [arrowTable, columns, viewParams?.segments]);

  // For the current segmentation, extract all measures and dates, and build a raw data matrix
  const rawData = useMemo(() => {
    if (
      !arrowTable ||
      !columns ||
      segmentationCombinations.length === 0 ||
      !viewParams?.segments ||
      viewParams.segments.length === 0
    ) {
      console.warn(
        "[SupplyWalkSingleViewerComponent] No arrowTable/columns/segmentationCombinations"
      );
      return null;
    }

    const currentCombination = segmentationCombinations[currentIndex];
    if (!currentCombination) return null;

    const colNames = new Set(columns.map((c) => c.name));
    const hasSegmentationCol = colNames.has("segmentation");
    const segmentColsMissing = viewParams.segments!.some(
      (s) => !colNames.has(s)
    );

    // Filter rows based on all segment values, using the same normalization
    // as segmentationCombinations so null/undefined/type differences don't
    // cause false mismatches.
    const filteredRows = Array.from({ length: arrowTable.numRows }, (_, i) =>
      getRowFromArrowTable(arrowTable, columns, i)
    ).filter((row) => {
      if (hasSegmentationCol && segmentColsMissing) {
        const segStr = normalizeSegmentValue(row.segmentation);
        const parts = segStr.split(" | ");
        return viewParams.segments!.every((segment, index) => {
          const expected = currentCombination.segmentValues[segment];
          const actual = parts[index] ?? "";
          return actual === expected;
        });
      }
      return viewParams.segments!.every((segment) => {
        const expectedValue = currentCombination.segmentValues[segment];
        const actualValue = normalizeSegmentValue(row[segment]);
        return actualValue === expectedValue;
      });
    });

    if (filteredRows.length === 0) {
      console.warn("[SupplyWalkSingleViewerComponent] No filteredRows");
      return null;
    }

    // Extract all unique measures and dates
    const measures = Array.from(
      new Set(filteredRows.map((row) => row.measure))
    );

    // Safety check: ensure we have a valid date hierarchy
    if (!viewParams.dateHierarchy) {
      console.warn(
        "[SupplyWalkSingleViewerComponent] No dateHierarchy in viewParams"
      );
      return null;
    }

    const dateField = viewParams.dateHierarchy.includes("day")
      ? "day"
      : viewParams.dateHierarchy.includes("week")
        ? "week"
        : viewParams.dateHierarchy.includes("month")
          ? "month"
          : "datetime_week";
    // Safety check: ensure the dateField exists in the data
    if (!filteredRows.some((row) => row[dateField])) {
      console.warn(
        `[SupplyWalkSingleViewerComponent] Date field '${dateField}' not found in data`
      );
      return null;
    }

    const allDates = Array.from(
      new Set(
        filteredRows
          .map((row) => {
            const dateValue = row[dateField];
            return dateValue ? dayjs.utc(dateValue).valueOf() : null;
          })
          .filter((ts) => ts !== null)
      )
    )
      .sort((a, b) => a - b)
      .map((ts) => dayjs.utc(ts));

    // Safety check: ensure we have valid dates
    if (allDates.length === 0) {
      console.warn("[SupplyWalkSingleViewerComponent] No valid dates found");
      return null;
    }

    // Find the first date with non-zero demand (forecasted sales)
    let startDateIndex = 0;
    for (let i = 0; i < allDates.length; i++) {
      const date = allDates[i];
      const forecastRow = filteredRows.find(
        (r) =>
          r.measure === "forecasted sales" &&
          r[dateField] &&
          r[dateField].valueOf() === date.valueOf()
      );
      const forecastValue = forecastRow?.value;
      const numericValue =
        typeof forecastValue === "string"
          ? parseFloat(forecastValue.replace(/,/g, ""))
          : Number(forecastValue);

      if (Math.abs(numericValue) > 0) {
        startDateIndex = i;
        break;
      }
    }

    // Use dates starting from the first non-zero demand date
    const dates = allDates.slice(startDateIndex);

    // Safety check: ensure we have valid dates
    if (dates.length === 0) {
      return { measures: [], dates: [], data: {} };
    }

    // Build a matrix: measure -> date -> value
    const data: Record<string, Record<string, string | number>> = {};
    measures.forEach((measure) => {
      data[measure] = {};
      dates.forEach((date) => {
        const row = filteredRows.find(
          (r) =>
            r.measure === measure &&
            r[dateField] &&
            r[dateField].valueOf() === date.valueOf()
        );
        let value = row?.value;
        if (typeof value === "string") value = value.trim();
        data[measure][String(date.valueOf())] = value ?? "";
      });
    });

    // "lead time" is a hidden field: kept in `data` so getLeadTimeValue can read
    // it for the Days Lead Time control, but excluded from the rendered rows.
    const displayMeasures = measures.filter(
      (m) => String(m).toLowerCase() !== "lead time"
    );
    return { measures: displayMeasures, dates, data };
  }, [
    arrowTable,
    columns,
    segmentationCombinations,
    currentIndex,
    viewParams?.segments,
    viewParams?.dateHierarchy,
  ]);

  if (loading) {
    <LoadingState />;
  }

  if (error) {
    return (
      <Box>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!arrowTable || segmentationCombinations.length === 0 || !rawData) {
    console.warn(
      "[SupplyWalkSingleViewerComponent] No data available: arrowTable:",
      !!arrowTable,
      "segmentationCombinations:",
      segmentationCombinations.length,
      "rawData:",
      !!rawData
    );
    return (
      <Box>
        <Typography variant="body1" color="text.secondary">
          No data available
        </Typography>
      </Box>
    );
  }

  if (!viewParams) return null;

  const currentCombination = segmentationCombinations[currentIndex];

  return (
    <Box>
      {/* Data table: raw values only, with editable cells */}
      <Box sx={{ flex: 1, overflowX: "auto" }}>
        <TableContainer component={Paper} sx={{ maxHeight: 600 }}>
          <Table size="small" stickyHeader sx={{ minWidth: 800 }}>
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{
                    fontWeight: "bold",
                    minWidth: 200,
                    position: "sticky",
                    left: 0,
                    backgroundColor: "white",
                    zIndex: 1,
                    borderRight: "2px solid #e0e0e0",
                    textAlign: "left",
                  }}
                >
                  measure
                </TableCell>
                {rawData.dates.map((date) => (
                  <TableCell
                    key={date.valueOf()}
                    sx={{
                      fontWeight: "bold",
                      minWidth: 80,
                      textAlign: "left",
                    }}
                  >
                    {shortFormatDateString(date)}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rawData.measures.map((measure) => (
                <TableRow key={measure} hover>
                  <TableCell
                    sx={{
                      fontWeight: "medium",
                      position: "sticky",
                      left: 0,
                      backgroundColor: "white",
                      zIndex: 1,
                      borderRight: "2px solid #e0e0e0",
                      textAlign: "left",
                    }}
                  >
                    {getDynamicMeasureLabel(
                      measure,
                      viewParams as DatePivotParams
                    )}
                  </TableCell>
                  {rawData.dates.map((date, dateIdx) => {
                    // Determine if this cell should be editable
                    const isEditable =
                      (measure.toLowerCase() === "starting inventory on hand" &&
                        dateIdx === 0) ||
                      measure.toLowerCase() === "forecasted sales" ||
                      measure.toLowerCase() === "purchase order arrivals" ||
                      measure.toLowerCase() === "purchase order to place";
                    // Show edited value if present, else raw value
                    let displayValue =
                      editedValues[currentCombination.displayKey]?.[measure]?.[
                        String(date.valueOf())
                      ] ??
                      rawData.data[measure][String(date.valueOf())] ??
                      "";

                    // Format numbers for key measures (except in edit mode)
                    const measuresToFormat = [
                      "starting inventory on hand",
                      "forecasted sales",
                      "purchase order arrivals",
                      "ending inventory on hand",
                      "unfulfilled demand",
                      "purchase order to place",
                    ];
                    const shouldFormat = measuresToFormat.includes(
                      measure.toLowerCase()
                    );
                    if (!isEditable || !editMode) {
                      if (shouldFormat && displayValue !== "") {
                        displayValue = String(formatInt(displayValue));
                      }
                    }
                    // For all numeric checks, strip commas from displayValue
                    const numericString = String(displayValue ?? "").replace(
                      /,/g,
                      ""
                    );
                    // For forecasted sales, always show negative if not empty
                    const isForecastedSales =
                      measure.toLowerCase() === "forecasted sales";
                    if (
                      !editMode &&
                      isForecastedSales &&
                      numericString !== ""
                    ) {
                      displayValue =
                        "-" + formatInt(Math.abs(Number(numericString)));
                    }
                    // Highlight purchase order to place/arrivals if not 0 (numeric)
                    const isPOPlaced =
                      measure.toLowerCase() === "purchase order to place";
                    const isPOArrivals =
                      measure.toLowerCase() === "purchase order arrivals";
                    let highlight = false;
                    if (
                      (isPOPlaced || isPOArrivals) &&
                      numericString !== "" &&
                      !isNaN(Number(numericString)) &&
                      Number(numericString) !== 0
                    ) {
                      highlight = true;
                    }
                    // Always compute targetWOS for the current cell
                    let targetWOS: number | null = null;
                    for (const m of rawData.measures) {
                      if (
                        m.toLowerCase() ===
                        getWOSMeasureKey(
                          "target",
                          viewParams?.dateHierarchy || ""
                        )
                      ) {
                        const targetValRaw =
                          editedValues[currentCombination.displayKey]?.[m]?.[
                            String(date.valueOf())
                          ] ??
                          rawData.data[m][String(date.valueOf())] ??
                          "";
                        const targetVal =
                          typeof targetValRaw === "string"
                            ? targetValRaw.replace(/,/g, "")
                            : targetValRaw;
                        if (targetVal !== "" && !isNaN(Number(targetVal))) {
                          targetWOS = Number(targetVal);
                          break;
                        }
                      }
                    }
                    // Determine if this cell has been edited directly by the user
                    const isEdited = Boolean(
                      editedValues[currentCombination.displayKey]?.[measure]?.[
                        String(date.valueOf())
                      ]
                    );
                    // Get the original value for tooltip
                    const originalValue =
                      rawData.data[measure][String(date.valueOf())] ?? "";
                    // Only show highlight for directly edited cells (not calculated ones)
                    const isDirectlyEdited =
                      isEdited &&
                      // Only highlight if this is an editable measure that was directly edited
                      ((measure.toLowerCase() ===
                        "starting inventory on hand" &&
                        dateIdx === 0) ||
                        measure.toLowerCase() === "forecasted sales" ||
                        measure.toLowerCase() === "purchase order arrivals" ||
                        measure.toLowerCase() === "purchase order to place");
                    const showHighlight = isDirectlyEdited && editMode;
                    return (
                      <TableCell
                        key={date.valueOf()}
                        sx={{
                          minWidth: 120,
                          backgroundColor: getCellBackgroundColor({
                            measure,
                            value: Number(numericString) || 0,
                            targetWOS,
                            highlight,
                          }),
                          fontWeight: showHighlight ? 700 : undefined,
                          fontSize: showHighlight ? "1.1em" : undefined,
                          transition: "all 0.2s",
                          position: "relative", // For positioning the triangle
                          textAlign: "left",
                        }}
                      >
                        {isDirectlyEdited ? (
                          <Tooltip title={`Original: ${originalValue}`} arrow>
                            <Box
                              sx={{
                                position: "relative",
                                width: "100%",
                                display: "inline-block",
                              }}
                            >
                              {/* Red triangle in corner */}
                              <Box
                                sx={{
                                  position: "absolute",
                                  top: 0,
                                  right: 0,
                                  width: 0,
                                  height: 0,
                                  borderStyle: "solid",
                                  borderWidth: "0 12px 12px 0",
                                  borderColor:
                                    "transparent " +
                                    theme.palette.atomic.red1 +
                                    " transparent transparent",
                                  zIndex: 1,
                                }}
                              />
                              <span
                                style={{
                                  width: "100%",
                                  display: "inline-block",
                                }}
                              >
                                {isEditable && editMode ? (
                                  isForecastedSales ? (
                                    <Box
                                      display="flex"
                                      alignItems="center"
                                      justifyContent="center"
                                    >
                                      <span
                                        style={{
                                          fontWeight: 500,
                                          marginRight: 2,
                                        }}
                                      >
                                        -
                                      </span>
                                      <CompactTableTextField
                                        value={
                                          localInputValues[
                                            currentCombination.displayKey
                                          ]?.[measure]?.[
                                            String(date.valueOf())
                                          ] ??
                                          (displayValue === ""
                                            ? ""
                                            : String(
                                                Math.abs(
                                                  Number(
                                                    displayValue.replace(
                                                      /,/g,
                                                      ""
                                                    )
                                                  )
                                                )
                                              ))
                                        }
                                        onChange={(e) => {
                                          const val = e.target.value.replace(
                                            /[^0-9]/g,
                                            ""
                                          );
                                          if (
                                            measure.toLowerCase() ===
                                            "purchase order to place"
                                          ) {
                                            // Only for PO placed: update arrivals as well
                                            const leadTimeRaw1 =
                                              getLeadTimeValue(
                                                editedValues,
                                                rawData,
                                                currentCombination.displayKey,
                                                rawData.dates[0]
                                                  .valueOf()
                                                  .toString()
                                              );
                                            const leadTimeDays1 =
                                              Number(leadTimeRaw1) || 0;
                                            const arrivalDateStr1 =
                                              findArrivalWeek(
                                                date.valueOf().toString(),
                                                leadTimeDays1,
                                                rawData.dates.map((d) =>
                                                  d.valueOf().toString()
                                                )
                                              );
                                            setLocalInputValues((prev) => ({
                                              ...prev,
                                              [currentCombination.displayKey]: {
                                                ...(prev[
                                                  currentCombination.displayKey
                                                ] || {}),
                                                [measure]: {
                                                  ...(prev[
                                                    currentCombination
                                                      .displayKey
                                                  ]?.[measure] || {}),
                                                  [String(date.valueOf())]: val,
                                                },
                                                "purchase order arrivals": {
                                                  ...(prev[
                                                    currentCombination
                                                      .displayKey
                                                  ]?.[
                                                    "purchase order arrivals"
                                                  ] || {}),
                                                  [arrivalDateStr1]: val,
                                                },
                                              },
                                            }));
                                          } else {
                                            // For all other measures, just update the value
                                            setLocalInputValues((prev) => ({
                                              ...prev,
                                              [currentCombination.displayKey]: {
                                                ...(prev[
                                                  currentCombination.displayKey
                                                ] || {}),
                                                [measure]: {
                                                  ...(prev[
                                                    currentCombination
                                                      .displayKey
                                                  ]?.[measure] || {}),
                                                  [String(date.valueOf())]: val,
                                                },
                                              },
                                            }));
                                          }
                                        }}
                                        onBlur={() => {
                                          const val =
                                            localInputValues[
                                              currentCombination.displayKey
                                            ]?.[measure]?.[
                                              String(date.valueOf())
                                            ] ?? "";
                                          if (
                                            measure.toLowerCase() ===
                                            "purchase order to place"
                                          ) {
                                            const leadTimeRaw1 =
                                              getLeadTimeValue(
                                                editedValues,
                                                rawData,
                                                currentCombination.displayKey,
                                                rawData.dates[0]
                                                  .valueOf()
                                                  .toString()
                                              );
                                            const leadTimeDays1 =
                                              Number(leadTimeRaw1) || 0;
                                            const arrivalDateStr1 =
                                              findArrivalWeek(
                                                date.valueOf().toString(),
                                                leadTimeDays1,
                                                rawData.dates.map((d) =>
                                                  d.valueOf().toString()
                                                )
                                              );
                                            if (editMode && val !== "") {
                                              setEditedValues((prev) => {
                                                const updated = {
                                                  ...prev,
                                                  [currentCombination.displayKey]:
                                                    {
                                                      ...(prev[
                                                        currentCombination
                                                          .displayKey
                                                      ] || {}),
                                                      "purchase order to place":
                                                        {
                                                          ...(prev[
                                                            currentCombination
                                                              .displayKey
                                                          ]?.[
                                                            "purchase order to place"
                                                          ] || {}),
                                                          [String(
                                                            date.valueOf()
                                                          )]: val,
                                                        },
                                                      "purchase order arrivals":
                                                        {
                                                          ...(prev[
                                                            currentCombination
                                                              .displayKey
                                                          ]?.[
                                                            "purchase order arrivals"
                                                          ] || {}),
                                                          [arrivalDateStr1]:
                                                            val,
                                                        },
                                                    },
                                                };
                                                return {
                                                  ...updated,
                                                  [currentCombination.displayKey]:
                                                    cascadeRecalculate(
                                                      "purchase order to place",
                                                      String(date.valueOf()),
                                                      val,
                                                      updated[
                                                        currentCombination
                                                          .displayKey
                                                      ],
                                                      rawData,
                                                      rawData.dates.map((d) =>
                                                        d.valueOf().toString()
                                                      ),
                                                      viewParams as DatePivotParams
                                                    ),
                                                };
                                              });
                                            }
                                          } else {
                                            if (editMode && val !== "") {
                                              setEditedValues((prev) => {
                                                return {
                                                  ...prev,
                                                  [currentCombination.displayKey]:
                                                    cascadeRecalculate(
                                                      measure,
                                                      String(date.valueOf()),
                                                      val,
                                                      prev[
                                                        currentCombination
                                                          .displayKey
                                                      ] || {},
                                                      rawData,
                                                      rawData.dates.map((d) =>
                                                        d.valueOf().toString()
                                                      ),
                                                      viewParams as DatePivotParams
                                                    ),
                                                };
                                              });
                                            }
                                          }
                                        }}
                                      />
                                    </Box>
                                  ) : (
                                    <CompactTableTextField
                                      value={
                                        localInputValues[
                                          currentCombination.displayKey
                                        ]?.[measure]?.[
                                          String(date.valueOf())
                                        ] ?? displayValue
                                      }
                                      onChange={(e) => {
                                        const val = e.target.value.replace(
                                          /[^0-9]/g,
                                          ""
                                        );
                                        if (
                                          measure.toLowerCase() ===
                                          "purchase order to place"
                                        ) {
                                          // Only for PO placed: update arrivals as well
                                          const leadTimeRaw1 = getLeadTimeValue(
                                            editedValues,
                                            rawData,
                                            currentCombination.displayKey,
                                            rawData.dates[0]
                                              .valueOf()
                                              .toString()
                                          );
                                          const leadTimeDays1 =
                                            Number(leadTimeRaw1) || 0;
                                          const arrivalDateStr1 =
                                            findArrivalWeek(
                                              date.valueOf().toString(),
                                              leadTimeDays1,
                                              rawData.dates.map((d) =>
                                                d.valueOf().toString()
                                              )
                                            );
                                          setLocalInputValues((prev) => ({
                                            ...prev,
                                            [currentCombination.displayKey]: {
                                              ...(prev[
                                                currentCombination.displayKey
                                              ] || {}),
                                              [measure]: {
                                                ...(prev[
                                                  currentCombination.displayKey
                                                ]?.[measure] || {}),
                                                [String(date.valueOf())]: val,
                                              },
                                              "purchase order arrivals": {
                                                ...(prev[
                                                  currentCombination.displayKey
                                                ]?.[
                                                  "purchase order arrivals"
                                                ] || {}),
                                                [arrivalDateStr1]: val,
                                              },
                                            },
                                          }));
                                        } else {
                                          // For all other measures, just update the value
                                          setLocalInputValues((prev) => ({
                                            ...prev,
                                            [currentCombination.displayKey]: {
                                              ...(prev[
                                                currentCombination.displayKey
                                              ] || {}),
                                              [measure]: {
                                                ...(prev[
                                                  currentCombination.displayKey
                                                ]?.[measure] || {}),
                                                [String(date.valueOf())]: val,
                                              },
                                            },
                                          }));
                                        }
                                      }}
                                      onBlur={() => {
                                        const val =
                                          localInputValues[
                                            currentCombination.displayKey
                                          ]?.[measure]?.[
                                            String(date.valueOf())
                                          ] ?? "";
                                        if (
                                          measure.toLowerCase() ===
                                          "purchase order to place"
                                        ) {
                                          const leadTimeRaw1 = getLeadTimeValue(
                                            editedValues,
                                            rawData,
                                            currentCombination.displayKey,
                                            rawData.dates[0]
                                              .valueOf()
                                              .toString()
                                          );
                                          const leadTimeDays1 =
                                            Number(leadTimeRaw1) || 0;
                                          const arrivalDateStr1 =
                                            findArrivalWeek(
                                              date.valueOf().toString(),
                                              leadTimeDays1,
                                              rawData.dates.map((d) =>
                                                d.valueOf().toString()
                                              )
                                            );
                                          if (editMode && val !== "") {
                                            setEditedValues((prev) => {
                                              const updated = {
                                                ...prev,
                                                [currentCombination.displayKey]:
                                                  {
                                                    ...(prev[
                                                      currentCombination
                                                        .displayKey
                                                    ] || {}),
                                                    "purchase order to place": {
                                                      ...(prev[
                                                        currentCombination
                                                          .displayKey
                                                      ]?.[
                                                        "purchase order to place"
                                                      ] || {}),
                                                      [String(date.valueOf())]:
                                                        val,
                                                    },
                                                    "purchase order arrivals": {
                                                      ...(prev[
                                                        currentCombination
                                                          .displayKey
                                                      ]?.[
                                                        "purchase order arrivals"
                                                      ] || {}),
                                                      [arrivalDateStr1]: val,
                                                    },
                                                  },
                                              };
                                              return {
                                                ...updated,
                                                [currentCombination.displayKey]:
                                                  cascadeRecalculate(
                                                    "purchase order to place",
                                                    String(date.valueOf()),
                                                    val,
                                                    updated[
                                                      currentCombination
                                                        .displayKey
                                                    ],
                                                    rawData,
                                                    rawData.dates.map((d) =>
                                                      d.valueOf().toString()
                                                    ),
                                                    viewParams as DatePivotParams
                                                  ),
                                              };
                                            });
                                          }
                                        } else {
                                          if (editMode && val !== "") {
                                            setEditedValues((prev) => {
                                              return {
                                                ...prev,
                                                [currentCombination.displayKey]:
                                                  cascadeRecalculate(
                                                    measure,
                                                    String(date.valueOf()),
                                                    val,
                                                    prev[
                                                      currentCombination
                                                        .displayKey
                                                    ] || {},
                                                    rawData,
                                                    rawData.dates.map((d) =>
                                                      d.valueOf().toString()
                                                    ),
                                                    viewParams as DatePivotParams
                                                  ),
                                              };
                                            });
                                          }
                                        }
                                      }}
                                    />
                                  )
                                ) : (
                                  displayValue
                                )}
                              </span>
                            </Box>
                          </Tooltip>
                        ) : isEditable && editMode ? (
                          isForecastedSales ? (
                            <Box
                              display="flex"
                              alignItems="center"
                              justifyContent="center"
                            >
                              <span style={{ fontWeight: 500, marginRight: 2 }}>
                                -
                              </span>
                              <CompactTableTextField
                                value={
                                  localInputValues[
                                    currentCombination.displayKey
                                  ]?.[measure]?.[String(date.valueOf())] ??
                                  (displayValue === ""
                                    ? ""
                                    : String(
                                        Math.abs(
                                          Number(displayValue.replace(/,/g, ""))
                                        )
                                      ))
                                }
                                onChange={(e) => {
                                  const val = e.target.value.replace(
                                    /[^0-9]/g,
                                    ""
                                  );
                                  if (
                                    measure.toLowerCase() ===
                                    "purchase order to place"
                                  ) {
                                    // Only for PO placed: update arrivals as well
                                    const leadTimeRaw1 = getLeadTimeValue(
                                      editedValues,
                                      rawData,
                                      currentCombination.displayKey,
                                      rawData.dates[0].valueOf().toString()
                                    );
                                    const leadTimeDays1 =
                                      Number(leadTimeRaw1) || 0;
                                    const arrivalDateStr1 = findArrivalWeek(
                                      date.valueOf().toString(),
                                      leadTimeDays1,
                                      rawData.dates.map((d) =>
                                        d.valueOf().toString()
                                      )
                                    );
                                    setLocalInputValues((prev) => ({
                                      ...prev,
                                      [currentCombination.displayKey]: {
                                        ...(prev[
                                          currentCombination.displayKey
                                        ] || {}),
                                        [measure]: {
                                          ...(prev[
                                            currentCombination.displayKey
                                          ]?.[measure] || {}),
                                          [String(date.valueOf())]: val,
                                        },
                                        "purchase order arrivals": {
                                          ...(prev[
                                            currentCombination.displayKey
                                          ]?.["purchase order arrivals"] || {}),
                                          [arrivalDateStr1]: val,
                                        },
                                      },
                                    }));
                                  } else {
                                    // For all other measures, just update the value
                                    setLocalInputValues((prev) => ({
                                      ...prev,
                                      [currentCombination.displayKey]: {
                                        ...(prev[
                                          currentCombination.displayKey
                                        ] || {}),
                                        [measure]: {
                                          ...(prev[
                                            currentCombination.displayKey
                                          ]?.[measure] || {}),
                                          [String(date.valueOf())]: val,
                                        },
                                      },
                                    }));
                                  }
                                }}
                                onBlur={() => {
                                  const val =
                                    localInputValues[
                                      currentCombination.displayKey
                                    ]?.[measure]?.[String(date.valueOf())] ??
                                    "";
                                  if (
                                    measure.toLowerCase() ===
                                    "purchase order to place"
                                  ) {
                                    const leadTimeRaw1 = getLeadTimeValue(
                                      editedValues,
                                      rawData,
                                      currentCombination.displayKey,
                                      rawData.dates[0].valueOf().toString()
                                    );
                                    const leadTimeDays1 =
                                      Number(leadTimeRaw1) || 0;
                                    const arrivalDateStr1 = findArrivalWeek(
                                      date.valueOf().toString(),
                                      leadTimeDays1,
                                      rawData.dates.map((d) =>
                                        d.valueOf().toString()
                                      )
                                    );
                                    if (editMode && val !== "") {
                                      setEditedValues((prev) => {
                                        const updated = {
                                          ...prev,
                                          [currentCombination.displayKey]: {
                                            ...(prev[
                                              currentCombination.displayKey
                                            ] || {}),
                                            "purchase order to place": {
                                              ...(prev[
                                                currentCombination.displayKey
                                              ]?.["purchase order to place"] ||
                                                {}),
                                              [String(date.valueOf())]: val,
                                            },
                                            "purchase order arrivals": {
                                              ...(prev[
                                                currentCombination.displayKey
                                              ]?.["purchase order arrivals"] ||
                                                {}),
                                              [arrivalDateStr1]: val,
                                            },
                                          },
                                        };
                                        return {
                                          ...updated,
                                          [currentCombination.displayKey]:
                                            cascadeRecalculate(
                                              "purchase order to place",
                                              String(date.valueOf()),
                                              val,
                                              updated[
                                                currentCombination.displayKey
                                              ],
                                              rawData,
                                              rawData.dates.map((d) =>
                                                d.valueOf().toString()
                                              ),
                                              viewParams as DatePivotParams
                                            ),
                                        };
                                      });
                                    }
                                  } else {
                                    if (editMode && val !== "") {
                                      setEditedValues((prev) => {
                                        return {
                                          ...prev,
                                          [currentCombination.displayKey]:
                                            cascadeRecalculate(
                                              measure,
                                              String(date.valueOf()),
                                              val,
                                              prev[
                                                currentCombination.displayKey
                                              ] || {},
                                              rawData,
                                              rawData.dates.map((d) =>
                                                d.valueOf().toString()
                                              ),
                                              viewParams as DatePivotParams
                                            ),
                                        };
                                      });
                                    }
                                  }
                                }}
                              />
                            </Box>
                          ) : (
                            <CompactTableTextField
                              value={
                                localInputValues[
                                  currentCombination.displayKey
                                ]?.[measure]?.[String(date.valueOf())] ??
                                displayValue
                              }
                              onChange={(e) => {
                                const val = e.target.value.replace(
                                  /[^0-9]/g,
                                  ""
                                );
                                if (
                                  measure.toLowerCase() ===
                                  "purchase order to place"
                                ) {
                                  // Only for PO placed: update arrivals as well
                                  const leadTimeRaw1 = getLeadTimeValue(
                                    editedValues,
                                    rawData,
                                    currentCombination.displayKey,
                                    rawData.dates[0].valueOf().toString()
                                  );
                                  const leadTimeDays1 =
                                    Number(leadTimeRaw1) || 0;
                                  const arrivalDateStr1 = findArrivalWeek(
                                    date.valueOf().toString(),
                                    leadTimeDays1,
                                    rawData.dates.map((d) =>
                                      d.valueOf().toString()
                                    )
                                  );
                                  setLocalInputValues((prev) => ({
                                    ...prev,
                                    [currentCombination.displayKey]: {
                                      ...(prev[currentCombination.displayKey] ||
                                        {}),
                                      [measure]: {
                                        ...(prev[
                                          currentCombination.displayKey
                                        ]?.[measure] || {}),
                                        [String(date.valueOf())]: val,
                                      },
                                      "purchase order arrivals": {
                                        ...(prev[
                                          currentCombination.displayKey
                                        ]?.["purchase order arrivals"] || {}),
                                        [arrivalDateStr1]: val,
                                      },
                                    },
                                  }));
                                } else {
                                  // For all other measures, just update the value
                                  setLocalInputValues((prev) => ({
                                    ...prev,
                                    [currentCombination.displayKey]: {
                                      ...(prev[currentCombination.displayKey] ||
                                        {}),
                                      [measure]: {
                                        ...(prev[
                                          currentCombination.displayKey
                                        ]?.[measure] || {}),
                                        [String(date.valueOf())]: val,
                                      },
                                    },
                                  }));
                                }
                              }}
                              onBlur={() => {
                                const val =
                                  localInputValues[
                                    currentCombination.displayKey
                                  ]?.[measure]?.[String(date.valueOf())] ?? "";
                                if (
                                  measure.toLowerCase() ===
                                  "purchase order to place"
                                ) {
                                  const leadTimeRaw1 = getLeadTimeValue(
                                    editedValues,
                                    rawData,
                                    currentCombination.displayKey,
                                    rawData.dates[0].valueOf().toString()
                                  );
                                  const leadTimeDays1 =
                                    Number(leadTimeRaw1) || 0;
                                  const arrivalDateStr1 = findArrivalWeek(
                                    date.valueOf().toString(),
                                    leadTimeDays1,
                                    rawData.dates.map((d) =>
                                      d.valueOf().toString()
                                    )
                                  );
                                  if (editMode && val !== "") {
                                    setEditedValues((prev) => {
                                      const updated = {
                                        ...prev,
                                        [currentCombination.displayKey]: {
                                          ...(prev[
                                            currentCombination.displayKey
                                          ] || {}),
                                          "purchase order to place": {
                                            ...(prev[
                                              currentCombination.displayKey
                                            ]?.["purchase order to place"] ||
                                              {}),
                                            [String(date.valueOf())]: val,
                                          },
                                          "purchase order arrivals": {
                                            ...(prev[
                                              currentCombination.displayKey
                                            ]?.["purchase order arrivals"] ||
                                              {}),
                                            [arrivalDateStr1]: val,
                                          },
                                        },
                                      };
                                      return {
                                        ...updated,
                                        [currentCombination.displayKey]:
                                          cascadeRecalculate(
                                            "purchase order to place",
                                            String(date.valueOf()),
                                            val,
                                            updated[
                                              currentCombination.displayKey
                                            ],
                                            rawData,
                                            rawData.dates.map((d) =>
                                              d.valueOf().toString()
                                            ),
                                            viewParams as DatePivotParams
                                          ),
                                      };
                                    });
                                  }
                                } else {
                                  if (editMode && val !== "") {
                                    setEditedValues((prev) => {
                                      return {
                                        ...prev,
                                        [currentCombination.displayKey]:
                                          cascadeRecalculate(
                                            measure,
                                            String(date.valueOf()),
                                            val,
                                            prev[
                                              currentCombination.displayKey
                                            ] || {},
                                            rawData,
                                            rawData.dates.map((d) =>
                                              d.valueOf().toString()
                                            ),
                                            viewParams as DatePivotParams
                                          ),
                                      };
                                    });
                                  }
                                }
                              }}
                            />
                          )
                        ) : (
                          displayValue
                        )}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
      {/* Header with navigation */}
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        sx={{
          border: "1px solid #e0e0e0",
          borderRadius: 1,
          p: 1,
          bgcolor: "#f5f5f5",
          mt: 2,
          // Pin the controls (segmentation ticker + Days Lead Time + Edit Mode)
          // to the bottom so they're always reachable even when the table is
          // tall — fixes the "scroll off-screen unless maximized" bug.
          position: "sticky",
          bottom: 0,
          zIndex: 2,
        }}
      >
        {/* Left side: Navigation controls and Segmentation info */}
        <Box display="flex" alignItems="center" gap={2}>
          <IconButton
            onClick={() => setCurrentIndex((i) => Math.max(i - 1, 0))}
            disabled={currentIndex === 0}
            size="small"
            sx={{
              bgcolor: "white",
              "&:hover": { bgcolor: theme.palette.action.hover },
            }}
          >
            <NavigateBeforeIcon />
          </IconButton>
          <Chip
            label={`${currentIndex + 1} of ${segmentationCombinations.length}`}
            variant="outlined"
            size="small"
            sx={{ fontWeight: "bold" }}
          />
          <IconButton
            onClick={() =>
              setCurrentIndex((i) =>
                Math.min(i + 1, segmentationCombinations.length - 1)
              )
            }
            disabled={currentIndex === segmentationCombinations.length - 1}
            size="small"
            sx={{
              bgcolor: "white",
              "&:hover": { bgcolor: theme.palette.action.hover },
            }}
          >
            <NavigateNextIcon />
          </IconButton>
          <Typography variant="body2" color="text.secondary">
            Segmentation: <strong>{currentCombination.displayKey}</strong>
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Rank: <strong>{currentCombination.segmentationRank}</strong>
          </Typography>
          {/* Editable Days Lead Time */}
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="body2" color="text.secondary">
              Days Lead Time:
            </Typography>
            <CompactTableTextField
              value={getLeadTimeValue(
                editedValues,
                rawData,
                currentCombination.displayKey,
                rawData.dates[0].valueOf().toString()
              )}
              onChange={(e) => {
                const val = e.target.value.replace(/[^0-9]/g, "");
                // Update lead time for all dates for this segmentation
                setEditedValues((prev) => ({
                  ...prev,
                  [currentCombination.displayKey]: {
                    ...(prev[currentCombination.displayKey] || {}),
                    "lead time": rawData.dates.reduce(
                      (acc, date) => {
                        acc[String(date.valueOf())] = val;
                        return acc;
                      },
                      {
                        ...(prev[currentCombination.displayKey]?.[
                          "lead time"
                        ] || {}),
                      }
                    ),
                  },
                }));
              }}
              onBlur={() => {
                if (!editMode) return;
                const segKey = currentCombination.displayKey;
                const firstDate = rawData.dates[0].valueOf().toString();
                const startInv =
                  editedValues[segKey]?.["starting inventory on hand"]?.[
                    firstDate
                  ] ??
                  rawData.data["starting inventory on hand"]?.[firstDate];
                if (startInv == null) return;
                // Re-cascade so ending inventory / WoS reflect current edits.
                // Future PO edits already pick up the new lead time via
                // getLeadTimeValue (findArrivalWeek = placement + this value).
                setEditedValues((prev) => ({
                  ...prev,
                  [segKey]: cascadeRecalculate(
                    "starting inventory on hand",
                    firstDate,
                    String(startInv),
                    prev[segKey] || {},
                    rawData,
                    rawData.dates.map((d) => d.valueOf().toString()),
                    viewParams as DatePivotParams
                  ),
                }));
              }}
            />
          </Box>
        </Box>
        {/* Right side: Edit Mode toggle */}
        <Box display="flex" alignItems="center" gap={2}>
          <FormControlLabel
            control={
              <Switch
                checked={editMode}
                onChange={(_, checked) => setEditMode(checked)}
                color="primary"
              />
            }
            label="Edit Mode"
            sx={{ ml: 2 }}
          />
        </Box>
      </Box>
    </Box>
  );
};
